/**
 * useMaintenanceConfig.js
 *
 * Hook for managing vessel-specific maintenance configuration settings.
 * WBC3 Edition: Settings for maintenance management
 *
 * Settings:
 * 1. Visible Categories - Which maintenance categories to display
 * 2. Advance Warning Days - Days before due to show "Due Soon" (7-90)
 * 3. Detailed Mode - Show individual equipment in job cards
 * 4. Show Completed Jobs - Toggle visibility of completed jobs
 * 5. Show Custom Jobs - Show user-created maintenance jobs
 * 6. Auto Generate Defects - Create defect when issues found (WBC3 3.5.7)
 * 7. Require Notes for Overdue - Require explanation for overdue jobs (WBC3 Appendix 8)
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';
import { MAINTENANCE_CATEGORIES } from '../../../config/maintenanceRequirements';

// RBAC helper (same pattern as other config hooks)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Categories that have regulatory requirements - hiding these shows a warning
 */
export const REGULATORY_CATEGORIES = ['lsa', 'ffe', 'surveys', 'radio', 'medical'];

/**
 * Default maintenance configuration
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Visible categories (all enabled by default)
  visibleCategories: Object.values(MAINTENANCE_CATEGORIES),

  // Setting 2: Days before due to show "Due Soon" warning
  advanceWarningDays: 30,

  // Setting 3: Show individual equipment in job cards
  detailedMode: false,

  // Setting 4: Show completed jobs in the list
  showCompletedJobs: false,

  // Setting 5: Show user-created custom jobs
  showCustomJobs: true,

  // Setting 6: Auto-create defect when maintenance issues found (WBC3 3.5.7)
  autoGenerateDefects: true,

  // Setting 7: Require notes when completing overdue jobs (WBC3 Appendix 8)
  requireNotesForOverdue: true,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing maintenance configuration
 */
export const useMaintenanceConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage - new key following pattern
  const storageKey = 'maintenance_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Legacy storage key for migration
  const { value: legacySettings, setValue: clearLegacy } = useStorageState(
    'maintenance_settings',
    null
  );

  // Migrate legacy settings on first load
  useEffect(() => {
    if (legacySettings && (!rawConfig?.version || rawConfig.version < 1)) {
      console.log('[useMaintenanceConfig] Migrating legacy maintenance_settings');
      const migratedConfig = {
        ...getDefaultConfig(),
        visibleCategories: legacySettings.visibleCategories || Object.values(MAINTENANCE_CATEGORIES),
        advanceWarningDays: legacySettings.advanceWarningDays || 30,
        detailedMode: legacySettings.detailedMode || false,
        showCustomJobs: legacySettings.showCustomJobs !== false,
        autoGenerateDefects: legacySettings.autoGenerateDefects !== false,
        lastModified: new Date().toISOString(),
        modifiedBy: { name: 'Migration', userId: 'system' }
      };
      setConfig(migratedConfig);
      // Clear legacy key after migration
      clearLegacy(null);
    }
  }, [legacySettings, rawConfig, setConfig, clearLegacy]);

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Check if any regulatory categories are hidden
   */
  const hiddenRegulatoryCategories = useMemo(() => {
    const visible = config.visibleCategories || [];
    return REGULATORY_CATEGORIES.filter(cat => !visible.includes(cat));
  }, [config.visibleCategories]);

  /**
   * Get count of visible categories
   */
  const visibleCategoryCount = useMemo(() => {
    return (config.visibleCategories || []).length;
  }, [config.visibleCategories]);

  /**
   * Total available categories
   */
  const totalCategories = Object.values(MAINTENANCE_CATEGORIES).length;

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useMaintenanceConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
        AUDIT_ACTIONS.UPDATE,
        'maintenance_config',
        {
          vesselId: currentVessel?.id,
          setting: settingKey,
          newValue: value,
          oldValue
        },
        { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useMaintenanceConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Toggle a category visibility
   */
  const toggleCategory = useCallback(async (category, enabled) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useMaintenanceConfig] Permission denied');
      return false;
    }

    const currentCategories = config.visibleCategories || [];
    let updatedCategories;

    if (enabled && !currentCategories.includes(category)) {
      updatedCategories = [...currentCategories, category];
    } else if (!enabled && currentCategories.includes(category)) {
      updatedCategories = currentCategories.filter(c => c !== category);
    } else {
      return true; // No change needed
    }

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      visibleCategories: updatedCategories,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
        AUDIT_ACTIONS.UPDATE,
        'maintenance_config_categories',
        {
          vesselId: currentVessel?.id,
          category,
          enabled,
          totalVisible: updatedCategories.length
        },
        { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useMaintenanceConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Update advance warning days
   */
  const setAdvanceWarningDays = useCallback(async (days) => {
    const clamped = Math.min(90, Math.max(7, days));
    return updateSetting('advanceWarningDays', clamped);
  }, [updateSetting]);

  /**
   * Toggle detailed mode
   */
  const setDetailedMode = useCallback(async (enabled) => {
    return updateSetting('detailedMode', !!enabled);
  }, [updateSetting]);

  /**
   * Toggle show completed jobs
   */
  const setShowCompletedJobs = useCallback(async (show) => {
    return updateSetting('showCompletedJobs', !!show);
  }, [updateSetting]);

  /**
   * Toggle show custom jobs
   */
  const setShowCustomJobs = useCallback(async (show) => {
    return updateSetting('showCustomJobs', !!show);
  }, [updateSetting]);

  /**
   * Toggle auto generate defects
   */
  const setAutoGenerateDefects = useCallback(async (enabled) => {
    return updateSetting('autoGenerateDefects', !!enabled);
  }, [updateSetting]);

  /**
   * Toggle require notes for overdue
   */
  const setRequireNotesForOverdue = useCallback(async (require) => {
    return updateSetting('requireNotesForOverdue', !!require);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useMaintenanceConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
        AUDIT_ACTIONS.UPDATE,
        'maintenance_config',
        { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
        { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useMaintenanceConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    hiddenRegulatoryCategories,
    visibleCategoryCount,
    totalCategories,

    // Direct settings access
    visibleCategories: config.visibleCategories,
    advanceWarningDays: config.advanceWarningDays,
    detailedMode: config.detailedMode,
    showCompletedJobs: config.showCompletedJobs,
    showCustomJobs: config.showCustomJobs,
    autoGenerateDefects: config.autoGenerateDefects,
    requireNotesForOverdue: config.requireNotesForOverdue,

    // Category mutations
    toggleCategory,

    // Individual mutations
    setAdvanceWarningDays,
    setDetailedMode,
    setShowCompletedJobs,
    setShowCustomJobs,
    setAutoGenerateDefects,
    setRequireNotesForOverdue,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    MAINTENANCE_CATEGORIES,
    REGULATORY_CATEGORIES
  };
};

export default useMaintenanceConfig;
